import React, { useEffect, useRef, useState } from 'react'
import OtpInputField from './OTPInputField'
import { useApp } from '../../../context/usePortal'
import SuccessPopup from './SuccessPopup'
import ChangePhoneNumber from '../../../components/ChangePhoneNumber'

interface CumulativeRangePopupProps {
  pin: string
  onClose: () => void
}

const VerifyOTPAndResetPIN: React.FC<CumulativeRangePopupProps> = ({
  pin,
  onClose,
}) => {
  const { resetPIN, user, getCode } = useApp()
  const [otp, setOtp] = useState<string>('')
  const [actionButtonAction, setActionButtonAction] = useState<string>('Verify')
  const [seconds, setSeconds] = useState<number>(60)
  const [timerActive, setTimerActive] = useState<boolean>(false)
  const [message, setMessage] = useState<string | undefined>('')
  const [resetOtp, setResetOtp] = useState<boolean>(true)
  const isMounted = useRef<boolean>(false)

  const phoneNumber = user?.country_code + '-' + user?.user_phone_number

  useEffect(() => {
    if (!isMounted.current) {
      sendOTP()
      isMounted.current = true
    }
  }, [])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null
    if (timerActive && seconds > 0) {
      intervalId = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1)
      }, 1000)
    } else if (seconds <= 0) {
      setActionButtonAction('resend OTP')
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [timerActive, seconds])

  const handleChangeOtp = (newValue: string = '') => {
    setOtp(newValue)
  }

  const handleVerifyOtpAndResetPIN = () => {
    const action = actionButtonAction.toLowerCase().trim()
    if (action === 'send otp' || action === 'resend otp') {
      sendOTP()
    } else if (action === 'verify') {
      VerifyOTP()
    }
  }

  const sendOTP = async () => {
    setResetOtp(true)
    const data = await getCode(phoneNumber, 'reset_pin')
    //@ts-ignore
    setMessage(data?.response?.message)
    if (data.isOk()) {
      setOtp('')
      setActionButtonAction('verify')
      setSeconds(60)
      setTimerActive(true)
    } else {
      setActionButtonAction('resend OTP')
    }
  }

  const VerifyOTP = async () => {
    if (otp.length !== 6) return

    setActionButtonAction('verifying')
    const { response } = await resetPIN(pin, otp, phoneNumber)
    setMessage(
      response?.message?.includes(':')
        ? response?.message?.split(':')[1]
        : response?.message,
    )
    if (response?.success) {
      setActionButtonAction('')
    } else {
      setActionButtonAction('resend OTP')
      setTimerActive(false)
    }
  }

  let action = actionButtonAction.toLowerCase().trim()
  let content: JSX.Element = <>{message}</>

  let buttonContent = <></>

  if (action === 'verifying') {
    buttonContent = (
      <div className="flex flex-col items-center justify-center">
        <span className="loader border-5 border-green-800"></span>
        <span className="mt-2 text-lg font-bold text-green-800">Verifying</span>
      </div>
    )
  } else if (action !== 'verifying') {
    buttonContent = (
      <button
        type="button"
        className={`${
          action === 'resend otp'
            ? 'text-green-800 hover:bg-green-800 hover:text-white'
            : 'bg-green-800 text-white'
        } mx-auto mt-5 block w-full rounded-3xl border-1 border-green-800 px-9 py-3 text-center font-bold capitalize`}
        onClick={handleVerifyOtpAndResetPIN}>
        {actionButtonAction}
      </button>
    )
  }

  return (
    <div>
      <h2 className="my-2 text-center text-xl font-bold">
        Mobile Phone Verification
      </h2>
      <h3 className="text-md text-center font-bold">One Time Password</h3>
      <OtpInputField
        length={6}
        onChangeOtp={handleChangeOtp}
        editable={action !== 'verify'}
        resetOtp={resetOtp}
      />
      {content}
      {buttonContent}
      
      <div className='mt-4 flex items-center justify-center'>
        <ChangePhoneNumber />
      </div>
    </div>
  )
}

export default VerifyOTPAndResetPIN
